import { z } from 'zod';

import { ApiError, apiRequest } from '@/api/client';

/**
 * NeonCarts' own published policies, mirroring `app/api/v1/legal.py`.
 *
 * Two audiences: `/legal/policies` is public - the registration form, the
 * marketing footer and shop footers link to it before anybody has signed in -
 * and `/legal/me` is the signed-in merchant's acceptance of them. Neither can
 * change a policy; that is the platform panel's, in `platform/api/policies.ts`.
 */

export const publicPolicySummarySchema = z.object({
  policy_type: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  requires_acceptance: z.boolean(),
  version_id: z.string(),
  version_number: z.number(),
  effective_date: z.string().nullable(),
  published_at: z.string().nullable(),
});

export const publicPolicySchema = publicPolicySummarySchema.extend({
  content: z.string(),
  change_summary: z.string().nullable(),
});

export const ACCEPTANCE_STATUSES = ['ACCEPTED', 'ACTION_REQUIRED', 'NOT_REQUIRED'] as const;

export const merchantPolicySchema = publicPolicySummarySchema.extend({
  change_summary: z.string().nullable(),
  acceptance_status: z.enum(ACCEPTANCE_STATUSES),
  accepted_version_number: z.number().nullable(),
  accepted_at: z.string().nullable(),
});

export const merchantPolicyOverviewSchema = z.object({
  policies: z.array(merchantPolicySchema),
  pending: z.array(merchantPolicySchema),
  can_accept: z.boolean(),
  /**
   * Whether the platform owner shows "Terms & Policies" in the merchant menu.
   * Optional so an older backend reads as hidden - the owner's default.
   */
  menu_visible: z.boolean().optional().default(false),
});

export type PublicPolicySummary = z.infer<typeof publicPolicySummarySchema>;
export type PublicPolicy = z.infer<typeof publicPolicySchema>;
export type MerchantPolicy = z.infer<typeof merchantPolicySchema>;
export type MerchantPolicyOverview = z.infer<typeof merchantPolicyOverviewSchema>;
export type AcceptanceStatus = (typeof ACCEPTANCE_STATUSES)[number];

export const PUBLIC_POLICIES_KEY = ['legal', 'policies'] as const;
export const MY_POLICIES_KEY = ['legal', 'me'] as const;

/** Where a published policy can be read by anybody, signed in or not. */
export function policyPath(slug: string): string {
  return `/legal/${encodeURIComponent(slug)}`;
}

export function fetchPublicPolicies() {
  return apiRequest('/legal/policies', z.array(publicPolicySummarySchema), { auth: 'none' });
}

export function fetchPublicPolicy(slug: string) {
  return apiRequest(`/legal/policies/${encodeURIComponent(slug)}`, publicPolicySchema, {
    auth: 'none',
  });
}

export function fetchMyPolicies() {
  return apiRequest('/legal/me', merchantPolicyOverviewSchema);
}

export function acceptPolicies(versionIds: string[], options: { prompted?: boolean } = {}) {
  return apiRequest('/legal/me/accept', merchantPolicyOverviewSchema, {
    method: 'POST',
    body: { version_ids: versionIds, prompted: options.prompted ?? false },
  });
}

const POLICY_ERRORS: Record<string, string> = {
  POLICY_VERSION_NOT_CURRENT:
    'One of these policies was updated a moment ago. Review the latest version and accept again.',
  POLICY_ACCEPTANCE_NOT_ALLOWED:
    'Only the business owner or an admin can accept NeonCarts policies.',
  POLICY_NOT_FOUND: 'This policy is not published.',
};

export function policyErrorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  if (error instanceof ApiError) return POLICY_ERRORS[error.code] ?? error.message;
  return 'Something went wrong. Try again.';
}
